import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { Monitor, Moon, Palette, Sparkles, Sun } from "@fleet/icons";
import { cn } from "@bodega/shared";

import { useNavigate } from "@tanstack/solid-router";
import { buttonVariants } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { type ColorMode, useTheme } from "@/providers/theme-provider";
import { useI18n } from "@/providers/i18n-provider";

/**
 * SINGLE appearance control in the header: color mode (System / Light /
 * Dark), blur effect, and access to the theme gallery.
 *
 * Previously there were THREE separate buttons in the upper right —blur,
 * appearance, and palette— for what is actually one family of settings. They
 * are combined into one, leaving two gaps for other utilities.
 *
 * Why THIS survives instead of the shortcut to the gallery, which was the
 * alternative: color mode changes several times a day and **works without a
 * session**, while `/themes` gates custom themes behind login. Leaving only
 * the link would leave guests unable to switch to dark mode, and the most
 * frequent action would cost a navigation and a return trip. In reverse it is
 * free: choosing a theme is rare and deliberate, so one extra click is not
 * noticeable.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function AppearanceMenu() {
  const theme = useTheme();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = createSignal(false);

  const choose = (mode: ColorMode) => {
    theme.selectColorMode(mode);
    setOpen(false);
  };

  const options: { mode: ColorMode; label: MessageDescriptor; icon: typeof Sun }[] = [
    { mode: "system", label: msg`System`, icon: Monitor },
    { mode: "light", label: msg`Light`, icon: Sun },
    { mode: "dark", label: msg`Dark`, icon: Moon },
  ];

  return (
    <Popover open={open()} onOpenChange={setOpen}>
      <PopoverTrigger
        as="button"
        type="button"
        class={cn(
          buttonVariants({ variant: "ghost", size: "icon" }),
          "h-8 w-8 text-muted-foreground hover:text-foreground",
        )}
        title={t(msg`Appearance`)}
        aria-label={t(msg`Appearance`)}
      >
        <Show when={theme.isDark()} fallback={<Sun class="h-4 w-4" />}>
          <Moon class="h-4 w-4" />
        </Show>
      </PopoverTrigger>
      <PopoverContent class="w-52 p-1.5" data-testid="appearance-menu">
        <p class="px-2 py-1 text-xs font-semibold text-muted-foreground">{t(msg`Appearance`)}</p>
        <For each={options}>
          {(opt) => (
            <button
              type="button"
              data-testid={`appearance-mode-${opt.mode}`}
              class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent/50 data-[active=true]:bg-accent/40 data-[active=true]:font-semibold"
              data-active={theme.colorMode() === opt.mode}
              onClick={() => choose(opt.mode)}
            >
              <opt.icon class="h-4 w-4" />
              <span>{t(opt.label)}</span>
            </button>
          )}
        </For>

        <div class="my-1 h-px bg-border" />

        {/* El desenfoque era el tercer botón de la cabecera. Aquí dice qué hace
            —como icono suelto no se sabía— y sigue mostrando su estado. */}
        <button
          type="button"
          data-testid="appearance-blur"
          role="switch"
          aria-checked={theme.isBlur()}
          class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent/50"
          onClick={() => {
            void theme.toggleBlur();
          }}
        >
          <Sparkles class={cn("h-4 w-4", theme.isBlur() && "text-primary")} />
          <span class="flex-1 text-left">{t(msg`Blur effect`)}</span>
          {/* El estado se transmite por el icono (color) y por `aria-checked`,
              no con un "On/Off" escrito. No es cosmético: cualquier cadena nueva
              saldría EN INGLÉS en los once idiomas, porque el gate de i18n solo
              valida lo que ya está en el catálogo y no obliga a extraer. Aquí
              todas las etiquetas existen y están traducidas. */}
          <span
            aria-hidden="true"
            class={cn(
              "h-2 w-2 rounded-full",
              theme.isBlur() ? "bg-primary" : "bg-muted-foreground/30",
            )}
          />
        </button>

        {/* Galería de temas: era el botón de la paleta. Se queda como último
            elemento porque es la única entrada que NAVEGA — el resto se aplica
            en el sitio y deja el menú donde estaba. */}
        <button
          type="button"
          data-testid="appearance-customize"
          class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent/50"
          onClick={() => {
            setOpen(false);
            void navigate({ to: "/themes" });
          }}
        >
          <Palette class="h-4 w-4" />
          {/* "Temas" en vez de "Personalizar": es el mismo destino y ya está
              traducida en los once idiomas. Una cadena nueva aquí se vería en
              inglés en 22 apps hasta que alguien haga una pasada de i18n. */}
          <span>{t(msg`Themes`)}</span>
        </button>
      </PopoverContent>
    </Popover>
  );
}
